import React, { useEffect, useState } from 'react';
import { collection, query, where, getDocs, doc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { User } from '../../types';
import './PendingRegistrations.css';

type ActionState = { userId: string; action: 'approve' | 'reject' } | null;

const PendingRegistrations: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<ActionState>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'real_estate_agent' | 'loan_originator'>('all');
  const { userProfile } = useAuth();

  useEffect(() => {
    fetchPending();
  }, []);

  const fetchPending = async () => {
    try {
      setLoading(true);
      setError(null);
      const q = query(
        collection(db, 'userProfiles'),
        where('verificationStatus', '==', 'pending')
      );
      const snapshot = await getDocs(q);
      const results = snapshot.docs.map(d => {
        const data = d.data();
        return {
          uid: d.id,
          ...data,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt
        } as User;
      });
      results.sort((a, b) => {
        if (!a.createdAt || !b.createdAt) return 0;
        const da = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt as any);
        const db_ = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt as any);
        return da.getTime() - db_.getTime(); // oldest first
      });
      setUsers(results);
    } catch (err: any) {
      setError(err.message || 'Failed to load registrations');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId: string) => {
    try {
      setProcessing(userId);
      await updateDoc(doc(db, 'userProfiles', userId), {
        verificationStatus: 'approved',
        approvedAt: Timestamp.now(),
        approvedBy: userProfile?.uid ?? 'admin'
      });
      setUsers(prev => prev.filter(u => u.uid !== userId));
      setConfirmAction(null);
    } catch (err: any) {
      setError(`Failed to approve: ${err.message}`);
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (userId: string) => {
    try {
      setProcessing(userId);
      const updateData: any = {
        verificationStatus: 'rejected',
        rejectedAt: Timestamp.now(),
        rejectedBy: userProfile?.uid ?? 'admin'
      };
      if (rejectReason.trim()) {
        updateData.rejectionReason = rejectReason.trim();
      }
      await updateDoc(doc(db, 'userProfiles', userId), updateData);
      setUsers(prev => prev.filter(u => u.uid !== userId));
      setConfirmAction(null);
      setRejectReason('');
    } catch (err: any) {
      setError(`Failed to reject: ${err.message}`);
    } finally {
      setProcessing(null);
    }
  };

  const filtered = users.filter(u =>
    filterType === 'all' ? true : u.userType === filterType
  );

  const agentCount = users.filter(u => u.userType === 'real_estate_agent').length;
  const lenderCount = users.filter(u => u.userType === 'loan_originator').length;

  return (
    <div className="pr-page">
      <div className="pr-header">
        <div>
          <h1 className="pr-title">Pending Registrations</h1>
          <p className="pr-subtitle">
            Review and approve new account registration requests.
            {users.length > 0 && <> <strong>{users.length}</strong> pending review.</>}
          </p>
        </div>
        <button className="pr-refresh-btn" onClick={fetchPending} disabled={loading}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="23 4 23 10 17 10"></polyline>
            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
          </svg>
          Refresh
        </button>
      </div>

      {error && (
        <div className="pr-error">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          {error}
        </div>
      )}

      {/* Filter Tabs */}
      <div className="pr-filter-bar">
        <button
          className={`pr-filter-btn ${filterType === 'all' ? 'active' : ''}`}
          onClick={() => setFilterType('all')}
        >
          All ({users.length})
        </button>
        <button
          className={`pr-filter-btn ${filterType === 'real_estate_agent' ? 'active' : ''}`}
          onClick={() => setFilterType('real_estate_agent')}
        >
          Real Estate Agents ({agentCount})
        </button>
        <button
          className={`pr-filter-btn ${filterType === 'loan_originator' ? 'active' : ''}`}
          onClick={() => setFilterType('loan_originator')}
        >
          Loan Originators ({lenderCount})
        </button>
      </div>

      {loading ? (
        <div className="pr-loading">
          <div className="pr-spinner"></div>
          <p>Loading registrations…</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="pr-empty">
          <div className="pr-empty-icon">✓</div>
          <h3>No Pending Registrations</h3>
          <p>{filterType === 'all' ? 'All registrations have been reviewed.' : 'No pending registrations for this role.'}</p>
        </div>
      ) : (
        <div className="pr-table-wrapper">
          <table className="pr-table">
            <thead>
              <tr>
                <th>Applicant</th>
                <th>Role</th>
                <th>License #</th>
                <th>Phone</th>
                <th>Phone Verified</th>
                <th>Email Verified</th>
                <th>Registered</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(user => (
                <tr key={user.uid}>
                  <td>
                    <div className="pr-user-cell">
                      <div className="pr-avatar">
                        {user.firstName?.[0]}{user.lastName?.[0]}
                      </div>
                      <div>
                        <div className="pr-user-name">{user.firstName} {user.lastName}</div>
                        <div className="pr-user-email">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={`pr-role-badge ${user.userType === 'loan_originator' ? 'lender' : 'agent'}`}>
                      {user.userType === 'loan_originator' ? '🏦 Loan Originator' : '🏠 Real Estate Agent'}
                    </span>
                  </td>
                  <td className="pr-license">
                    {user.nmls ? (
                      <span title={user.userType === 'loan_originator' ? 'NMLS #' : 'MLS #'}>
                        {user.userType === 'loan_originator' ? 'NMLS: ' : 'MLS: '}<strong>{user.nmls}</strong>
                      </span>
                    ) : <span className="pr-muted">—</span>}
                  </td>
                  <td className="pr-phone">{user.phoneNumber || <span className="pr-muted">—</span>}</td>
                  <td className="pr-verified-cell">
                    <VerifiedBadge verified={user.phoneVerified} />
                  </td>
                  <td className="pr-verified-cell">
                    <VerifiedBadge verified={user.emailVerified} />
                  </td>
                  <td className="pr-date">{fmtDate(user.createdAt)}</td>
                  <td>
                    <div className="pr-actions">
                      <button
                        className="pr-approve-btn"
                        onClick={() => setConfirmAction({ userId: user.uid, action: 'approve' })}
                        disabled={processing === user.uid}
                      >
                        Approve
                      </button>
                      <button
                        className="pr-reject-btn"
                        onClick={() => { setConfirmAction({ userId: user.uid, action: 'reject' }); setRejectReason(''); }}
                        disabled={processing === user.uid}
                      >
                        Reject
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Confirm Modal */}
      {confirmAction && (() => {
        const target = users.find(u => u.uid === confirmAction.userId);
        if (!target) return null;
        const isApprove = confirmAction.action === 'approve';
        return (
          <div className="pr-modal-overlay" onClick={() => !processing && setConfirmAction(null)}>
            <div className="pr-modal" onClick={e => e.stopPropagation()}>
              <div className={`pr-modal-icon ${isApprove ? 'approve' : 'reject'}`}>
                {isApprove ? '✓' : '✕'}
              </div>
              <h3>{isApprove ? 'Approve Registration' : 'Reject Registration'}</h3>
              <p>
                {isApprove
                  ? <>Approve <strong>{target.firstName} {target.lastName}</strong>'s account? They will receive an approval email and gain access to ScoreMyOffer.</>
                  : <>Reject <strong>{target.firstName} {target.lastName}</strong>'s registration? They will receive a rejection notification.</>
                }
              </p>

              {!isApprove && (
                <div className="pr-modal-reason">
                  <label htmlFor="rejectReason">Reason (optional)</label>
                  <textarea
                    id="rejectReason"
                    rows={3}
                    value={rejectReason}
                    onChange={e => setRejectReason(e.target.value)}
                    placeholder="Provide a reason for the applicant…"
                    disabled={!!processing}
                  />
                </div>
              )}

              <div className="pr-modal-actions">
                <button
                  className="pr-modal-cancel"
                  onClick={() => setConfirmAction(null)}
                  disabled={!!processing}
                >
                  Cancel
                </button>
                <button
                  className={isApprove ? 'pr-modal-approve' : 'pr-modal-reject'}
                  onClick={() => isApprove ? handleApprove(confirmAction.userId) : handleReject(confirmAction.userId)}
                  disabled={!!processing}
                >
                  {processing === confirmAction.userId
                    ? (isApprove ? 'Approving…' : 'Rejecting…')
                    : (isApprove ? 'Approve' : 'Reject')}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

function VerifiedBadge({ verified }: { verified?: boolean }) {
  if (verified) {
    return <span className="pr-verified pr-verified--yes">✓ Yes</span>;
  }
  return <span className="pr-verified pr-verified--no">✗ No</span>;
}

function fmtDate(val: any): string {
  if (!val) return '—';
  try {
    const d = val instanceof Date ? val : (val.toDate ? val.toDate() : new Date(val));
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return '—'; }
}

export default PendingRegistrations;
